import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { listImageHistory, getPublicHistoryEnabled, toggleFilePublicVisibility } from '../../services/historyApi';
import AppFooter from '../AppFooter';
import { FaArrowLeft, FaHome, FaEye, FaEyeSlash } from 'react-icons/fa';
import SearchFilterBar from '../shared/SearchFilterBar';
import MediaViewer from '../media/MediaViewer';

export default function EventHistory({ pageSize = 100 }) {
  const { tenant } = useParams();
  const tenantNum = Number(tenant) || null;

  const [items, setItems] = useState([]);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [publicHistoryEnabled, setPublicHistoryEnabled] = useState(false);
  const [togglingId, setTogglingId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [mediaViewer, setMediaViewer] = useState(null);

  const navigate = useNavigate();

  const fetchPage = async (reset = false) => {
    if (!tenantNum) return;
    setLoading(true);
    setErr(null);
    try {
      const res = await listImageHistory({
        tenant: tenantNum,
        limit: pageSize,
        offset: reset ? 0 : offset,
      });

      setItems(prev => (reset ? res.items : [...prev, ...res.items]));
      setOffset(prev => (reset ? res.items.length : prev + res.items.length));
    } catch (e) {
      setErr(e.message || 'Failed to load history');
    } finally {
      setLoading(false);
    }
  };

  // Fetch tenant's public_event_history_enabled setting
  useEffect(() => {
    if (!tenantNum) return;
    getPublicHistoryEnabled(tenantNum)
      .then(res => setPublicHistoryEnabled(res.enabled))
      .catch(() => setPublicHistoryEnabled(false));
  }, [tenantNum]);

  useEffect(() => {
    setItems([]);
    setOffset(0);
    if (tenantNum) fetchPage(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantNum]);

  const filteredItems = useMemo(() => {
    let result = items;
    const q = searchTerm.trim().toLowerCase();
    if (q) {
      result = result.filter((it) => {
        const title = (it.title || "").toLowerCase();
        const kind = (it.kind || "").toLowerCase();
        return title.includes(q) || kind.includes(q);
      });
    }
    if (dateFrom) {
      const from = new Date(dateFrom);
      result = result.filter((it) => it.createdOn && new Date(it.createdOn) >= from);
    }
    if (dateTo) {
      const to = new Date(dateTo + "T23:59:59");
      result = result.filter((it) => it.createdOn && new Date(it.createdOn) <= to);
    }
    // Sort by date descending
    result.sort((a, b) => {
      const da = new Date(a.createdOn || 0);
      const db = new Date(b.createdOn || 0);
      return db - da;
    });
    return result;
  }, [items, searchTerm, dateFrom, dateTo]);

  const groups = useMemo(() => {
    const by = {};
    for (const it of filteredItems) {
      const d = new Date(it.createdOn);
      if (!Number.isNaN(d.getTime())) {
        const key = d.toISOString().slice(0, 10);
        (by[key] ||= []).push(it);
      }
    }
    return Object.entries(by).sort(([a], [b]) => (a < b ? 1 : -1));
  }, [filteredItems]);

  const handleTogglePublic = useCallback(async (it) => {
    if (!tenantNum || !it.fileId) return;
    const newShow = !it.showInPublicHistory;
    setTogglingId(it.fileId);
    try {
      await toggleFilePublicVisibility(tenantNum, it.fileId, newShow);
      setItems(prev => prev.map(item =>
        item.fileId === it.fileId
          ? { ...item, showInPublicHistory: newShow }
          : item
      ));
    } catch (e) {
      console.error('Failed to toggle public visibility', e);
    } finally {
      setTogglingId(null);
    }
  }, [tenantNum]);

  const handleDashboard = () => {
    navigate(`/${tenant}/dashboard`);
  };

  return (
    <div className="container-fluid">
      <div className="flex items-center justify-between mb-3">
        <h3 className="m-0">Event History</h3>
      </div>

      <SearchFilterBar
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        dateFrom={dateFrom}
        dateTo={dateTo}
        onDateFromChange={setDateFrom}
        onDateToChange={setDateTo}
        onClear={() => { setSearchTerm(""); setDateFrom(""); setDateTo(""); }}
        searchPlaceholder="Search history\u2026"
      />

      {err && <div className="alert alert-danger">{err}</div>}

      {groups.map(([date, arr]) => (
        <div key={date} className="mb-4">
          <h5 className="mb-3">{date}</h5>
          <div className="row gap-3" style={{ alignItems: 'stretch' }}>
            {arr.map((it) => (
              <div key={`${it.kind}-${it.fileId}`} className="col-12 col-sm-6 col-md-4 col-lg-3">
                <div className="card h-full shadow-sm">
                  <div
                    className="ratio ratio-4x3 bg-dark-bg flex items-center justify-center"
                    style={{ cursor: "pointer" }}
                    role="button"
                    tabIndex={0}
                    onClick={() => setMediaViewer({ url: it.url, isVideo: it.mimetype?.startsWith('video/') })}
                    onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && setMediaViewer({ url: it.url, isVideo: it.mimetype?.startsWith('video/') })}
                  >
                    {it.mimetype?.startsWith('video/') ? (
                      <video
                        src={it.url}
                        muted
                        playsInline
                        preload="metadata"
                        style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', objectPosition: 'center' }}
                      />
                    ) : (
                      <img
                        src={it.url}
                        alt={it.title || 'image'}
                        className="img-fluid"
                        loading="lazy"
                        style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', objectPosition: 'center' }}
                      />
                    )}
                  </div>
                  <div className="card-body py-2">
                    <div className="small text-gray-400">
                      {it.kind === 'item' ? `Event Item #${it.eventItemId}` : `Event #${it.eventId ?? ''}`}
                    </div>
                    <div className="font-semibold truncate">{it.title}</div>
                    {/* Public visibility toggle for all media when tenant has public history enabled */}
                    {publicHistoryEnabled && (
                      <button
                        type="button"
                        className={`btn btn-sm mt-1 ${it.showInPublicHistory ? 'btn-outline-success' : 'btn-outline-secondary'}`}
                        onClick={() => handleTogglePublic(it)}
                        disabled={togglingId === it.fileId}
                        title={it.showInPublicHistory ? 'Visible in public history' : 'Hidden from public history'}
                      >
                        {it.showInPublicHistory ? <FaEye className="mr-1" /> : <FaEyeSlash className="mr-1" />}
                        {it.showInPublicHistory ? 'Public' : 'Hidden'}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
      <AppFooter
        buttons={[
          {
            icon: <FaArrowLeft />,
            label: "Back",
            onClick: () => navigate(-1),
          },
          {
            icon: <FaHome />,
            label: 'Home',
            onClick: handleDashboard,
          },
        ]}
      />
      {!loading && items.length === 0 && <div className="text-gray-400">No images yet.</div>}
      {!loading && items.length > 0 && filteredItems.length === 0 && (
        <div className="text-gray-400 text-center py-3">No items match your filters.</div>
      )}
      {loading && <div className="text-gray-400">Loading…</div>}
      {mediaViewer && (
        <MediaViewer
          src={mediaViewer.url}
          isVideo={mediaViewer.isVideo}
          onClose={() => setMediaViewer(null)}
        />
      )}
    </div>
  );
}
